import { useAccount } from 'wagmi';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useToast } from "@/components/ui/use-toast";
import Header from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { PLATFORM_FEE_COLLECTOR } from "@/utils/referralSystem";
import { fetchPlatformMetrics } from "@/utils/platformMetrics";
import { Loader2 } from "lucide-react";

const PlatformMetrics = () => {
  const { address } = useAccount();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: metricsData, isLoading, error } = useQuery({
    queryKey: ['platform-metrics'],
    queryFn: fetchPlatformMetrics,
    meta: {
      onError: (error: Error) => {
        toast({
          title: "Error fetching metrics",
          description: error.message || "Failed to load platform metrics",
          variant: "destructive",
        });
      },
    },
  });

  useEffect(() => {
    if (address?.toLowerCase() !== PLATFORM_FEE_COLLECTOR.toLowerCase()) {
      navigate('/');
    }
  }, [address, navigate]);

  if (address?.toLowerCase() !== PLATFORM_FEE_COLLECTOR.toLowerCase()) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!metricsData) {
    return null;
  }

  const currentMetrics = {
    monthlyTransactions: metricsData[metricsData.length - 1]?.actualTransactions || 0,
    monthlyUsers: metricsData[metricsData.length - 1]?.actualUsers || 0,
    projectedRevenue: metricsData[metricsData.length - 1]?.projectedRevenue || 0,
    averageTransactionSize: metricsData[metricsData.length - 1]?.actualRevenue 
      ? Math.round(metricsData[metricsData.length - 1].actualRevenue / metricsData[metricsData.length - 1].actualTransactions) 
      : 0,
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 pt-24 pb-16">
        <h1 className="text-3xl font-bold mb-8">Platform Metrics</h1>

        {/* Current Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-sm">Monthly Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{currentMetrics.monthlyTransactions}</div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-sm">Active Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{currentMetrics.monthlyUsers}</div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-sm">Projected Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${currentMetrics.projectedRevenue}</div>
            </CardContent>
          </Card>
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-sm">Avg Transaction Size</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${currentMetrics.averageTransactionSize}</div>
            </CardContent>
          </Card>
        </div>

        {/* Comparative Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Transactions Chart */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Transaction Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={metricsData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="month" className="text-muted-foreground" />
                    <YAxis className="text-muted-foreground" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        border: '1px solid rgba(255,255,255,0.1)'
                      }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="actualTransactions" 
                      name="Actual"
                      stroke="#8247E5" 
                      strokeWidth={2}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="projectedTransactions" 
                      name="Projected"
                      stroke="#E5DEFF" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Revenue Chart */}
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Revenue Comparison</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={metricsData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="month" className="text-muted-foreground" />
                    <YAxis className="text-muted-foreground" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(0,0,0,0.8)',
                        border: '1px solid rgba(255,255,255,0.1)'
                      }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="actualRevenue" 
                      name="Actual"
                      stroke="#4CAF50" 
                      strokeWidth={2}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="projectedRevenue" 
                      name="Projected"
                      stroke="#A5D6A7" 
                      strokeWidth={2}
                      strokeDasharray="5 5"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default PlatformMetrics;